import Comment from '@/models/Comment';
import VariationOrderCost from '@/models/VariationOrderCost';
import VariationOrderCostService from '@/services/VariationOrderCostService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useVariationOrderCostsStore = defineStore('variation_order_costs', {
  state: () => ({}),

  getters: {
    models: () => useRepo(VariationOrderCost),
  },

  actions: {
    async createVariationOrderCost(
      claimId: Id,
      tradeItemId: Id,
      request: ApiRequests['api.v1.variation-order-costs.store'],
    ) {
      const [data, error] = await VariationOrderCostService.create(claimId, tradeItemId, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async updateVariationOrderCost(id: Id, request: ApiRequests['api.v1.variation-order-costs.update']) {
      const [data, error] = await VariationOrderCostService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async updateComment(
      variationOrderCostId: Id,
      commentId: Id,
      request: ApiRequests['api.v1.variation-order-costs.comments.update'],
    ) {
      const [data, error] = await VariationOrderCostService.updateComment(variationOrderCostId, commentId, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async deleteComment(variationOrderCostId: Id, commentId: Id) {
      const [data, error] = await VariationOrderCostService.deleteComment(variationOrderCostId, commentId);

      if(error) {
        throw error;
      }

      useRepo(Comment).destroy(data.id);

      return data;
    },

    async deleteVariationOrderCost(id: Id) {
      const [data, error] = await VariationOrderCostService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async saveClaim(claimId: Id, signedParams: unknown, request: ApiRequests['api.v1.claims.variation.claim.store']) {
      const [data, error] = await VariationOrderCostService.claim(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async saveCertified(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.claims.variation.certified.store'],
    ) {
      const [data, error] = await VariationOrderCostService.certified(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async saveSubcontractorCertified(
      projectId: Id,
      claimId: Id,
      variationOrderCostId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.variation.certified.store'],
    ) {
      const [data, error] = await VariationOrderCostService.subcontractorCertified(
        projectId,
        claimId,
        variationOrderCostId,
        request,
      );

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async deleteSubcontractorCertified(
      projectId: Id,
      claimId: Id,
      variationOrderCostId: Id,
    ) {
      const [data, error] = await VariationOrderCostService.subcontractorDeleteCertified(
        projectId,
        claimId,
        variationOrderCostId,
      );

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async saveSubcontractorClaim(
      claimId: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.subcontractor.claims.variation.claim.store'],
    ) {
      const [data, error] = await VariationOrderCostService.subcontractorClaim(claimId, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteSubcontractorClaim(
      claimId: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.subcontractor.claims.variation.claim.destroy'],
    ) {
      const [data, error] = await VariationOrderCostService.subcontractorDelete(claimId, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async storeSignedComment(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.claims.variation-order-costs.comments.store.signed'],
    ) {
      const [data, error] = await VariationOrderCostService.comments.storeSigned(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async updateSignedComment(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.claims.variation-order-costs.comments.update.signed'],
    ) {
      const [data, error] = await VariationOrderCostService.comments.updateSigned(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },
  },
});
